<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-show="isOpen"
        class="fixed inset-0 z-50 bg-black"
        @touchstart="handleTouchStart"
        @touchend="handleTouchEnd"
      >
        <!-- Header with counter and close button -->
        <div class="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/50 to-transparent">
          <span class="text-white text-lg font-medium">
            {{ currentIndex + 1 }} / {{ images.length }}
          </span>
          <div class="flex items-center gap-2">
            <button
              @click="toggleZoom"
              class="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              :title="isZoomed ? 'Diminuir' : 'Ampliar'"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  v-if="!isZoomed"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                />
              </svg>
            </button>
            <button
              @click="close"
              class="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
              title="Fechar"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Main image container -->
        <div
          class="absolute inset-0 flex items-center justify-center"
          @click.self="close"
        >
          <div
            class="relative w-full h-full flex items-center justify-center overflow-hidden"
          >
            <img
              v-for="(image, index) in images"
              :key="index"
              :src="image"
              class="absolute max-w-full max-h-full object-contain transition-all duration-300"
              :class="[
                currentIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none',
                isZoomed ? 'scale-200 cursor-zoom-out' : 'cursor-zoom-in'
              ]"
              :style="isZoomed && currentIndex === index ? zoomStyle : {}"
              @click="toggleZoom"
              @load="onImageLoad(index)"
              :alt="`Imagem ${index + 1}`"
              draggable="false"
            />

            <!-- Loading spinner -->
            <div
              v-if="isLoading"
              class="absolute inset-0 flex items-center justify-center"
            >
              <div class="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          </div>
        </div>

        <!-- Navigation arrows -->
        <button
          @click="prev"
          class="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 text-white hover:bg-white/20 rounded-full transition-colors"
          title="Anterior"
        >
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <button
          @click="next"
          class="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 text-white hover:bg-white/20 rounded-full transition-colors"
          title="Seguinte"
        >
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Thumbnail strip -->
        <div class="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-black/50 to-transparent">
          <div class="flex gap-2 overflow-x-auto justify-center pb-2">
            <button
              v-for="(image, index) in images"
              :key="index"
              @click="goTo(index)"
              class="flex-shrink-0 w-16 h-12 rounded overflow-hidden transition-all duration-200"
              :class="currentIndex === index ? 'ring-2 ring-white scale-110' : 'opacity-60 hover:opacity-100'"
            >
              <img
                :src="image"
                class="w-full h-full object-cover"
                :alt="`Miniatura ${index + 1}`"
                loading="lazy"
              />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const images = ref([])

// Generate image paths
for (let i = 0; i <= 49; i++) {
  images.value.push(require(`@/assets/images/gallery/${i}.jpg`))
}

const isOpen = ref(false)
const currentIndex = ref(0)
const isZoomed = ref(false)
const isLoading = ref(false)
const loadedImages = ref(new Set())

let touchStartX = 0
let touchStartY = 0

const zoomStyle = computed(() => {
  return {
    transform: 'scale(2)',
    cursor: 'zoom-out'
  }
})

const start = (startIndex = 0) => {
  currentIndex.value = startIndex
  isOpen.value = true
  isZoomed.value = false
  document.body.style.overflow = 'hidden'
  preloadAdjacentImages()
}

const close = () => {
  isOpen.value = false
  isZoomed.value = false
  document.body.style.overflow = ''
}

const next = () => {
  isZoomed.value = false
  currentIndex.value = (currentIndex.value + 1) % images.value.length
  preloadAdjacentImages()
}

const prev = () => {
  isZoomed.value = false
  currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length
  preloadAdjacentImages()
}

const goTo = (index) => {
  isZoomed.value = false
  currentIndex.value = index
  preloadAdjacentImages()
}

const toggleZoom = () => {
  isZoomed.value = !isZoomed.value
}

const onImageLoad = (index) => {
  loadedImages.value.add(index)
  if (index === currentIndex.value) {
    isLoading.value = false
  }
}

const preloadAdjacentImages = () => {
  const indicesToPreload = [
    currentIndex.value,
    (currentIndex.value + 1) % images.value.length,
    (currentIndex.value - 1 + images.value.length) % images.value.length
  ]

  indicesToPreload.forEach((index) => {
    if (!loadedImages.value.has(index)) {
      const img = new Image()
      img.src = images.value[index]
    }
  })
}

const handleTouchStart = (e) => {
  touchStartX = e.touches[0].clientX
  touchStartY = e.touches[0].clientY
}

const handleTouchEnd = (e) => {
  if (isZoomed.value) return

  const touchEndX = e.changedTouches[0].clientX
  const touchEndY = e.changedTouches[0].clientY
  const diffX = touchStartX - touchEndX
  const diffY = touchStartY - touchEndY

  // Only swipe horizontally if the horizontal movement is greater than vertical
  if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
    if (diffX > 0) {
      next()
    } else {
      prev()
    }
  }
}

const handleKeydown = (e) => {
  if (!isOpen.value) return

  switch (e.key) {
    case 'Escape':
      close()
      break
    case 'ArrowLeft':
      prev()
      break
    case 'ArrowRight':
      next()
      break
    case ' ':
      e.preventDefault()
      toggleZoom()
      break
  }
}

watch(isOpen, (val) => {
  if (!val) {
    isLoading.value = false
  }
})

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})

defineExpose({ start, close })
</script>

<style scoped>
.scale-200 {
  transform: scale(2);
}
</style>
